/**
 * Real-time LOCAL goal / red-card alerts for the match a user is actively
 * watching, driven by the live Bzzoiro WebSocket (see useLiveBallTracker).
 *
 * Unlike the server-push live-score sticky (live.ts), these fire on-device the
 * instant the socket reports a score increment or a red card — no round-trip, no
 * server. They are immediate (null trigger on iOS / the high-importance
 * `match-alerts` channel on Android) and best-effort: a fire that fails (no
 * permission, channel not ready) is swallowed so the ball-tracker never breaks.
 *
 * Gating: we only fire when (1) we are NOT on web, (2) OS notification permission
 * is granted, and (3) the user's `goal_notifications` preference is on. The
 * permission + pref are cached briefly so the hot path (a goal frame) does no
 * blocking I/O on every event.
 */
import * as Notifications from 'expo-notifications';
import { Platform } from 'react-native';

import { i18n } from '@/lib/i18n';
import { getMyPushPrefs } from '@/lib/notifications';

const VOLT = '#22D3FF';

/** The shape the live tracker hands us for a goal alert. */
export interface GoalAlert {
  homeShort: string;
  awayShort: string;
  home: number;
  away: number;
  /** The scorer, when the latest action carried one. */
  scorer?: string | null;
  /**
   * The fixture (route) UUID, so a tap routes to /match/<id>. Optional so a call
   * without it still fires (it just won't be tappable to the match).
   */
  fixtureId?: string | null;
}

/** The shape the live tracker hands us for a red-card alert. */
export interface RedCardAlert {
  player?: string | null;
  /** The fixture (route) UUID, so a tap routes to /match/<id>. */
  fixtureId?: string | null;
}

// ---------------------------------------------------------------------------
// Cheap gate — cache the OS permission + the goal-notifications pref so the goal
// hot path never blocks. Re-checked every CACHE_TTL_MS.
// ---------------------------------------------------------------------------

const CACHE_TTL_MS = 60_000;

interface GateCache {
  at: number;
  enabled: boolean;
}

let gateCache: GateCache | null = null;

async function notificationsEnabled(): Promise<boolean> {
  if (Platform.OS === 'web') return false;

  const now = Date.now();
  if (gateCache && now - gateCache.at < CACHE_TTL_MS) return gateCache.enabled;

  let enabled = false;
  try {
    const permission = await Notifications.getPermissionsAsync();
    if (permission.status === 'granted') {
      // Respect the user's goal-notifications switch when we can read it; if the
      // pref lookup fails (offline / signed-out) we default to ON so a watcher
      // with granted permission still gets the alert.
      let goalsOn = true;
      try {
        goalsOn = (await getMyPushPrefs()).goalNotifications;
      } catch {
        goalsOn = true;
      }
      enabled = goalsOn;
    }
  } catch {
    enabled = false;
  }

  gateCache = { at: now, enabled };
  return enabled;
}

/** Drop the cached gate so the next alert re-reads permission + prefs. */
export function resetLiveEventGate(): void {
  gateCache = null;
}

async function ensureChannel(): Promise<void> {
  if (Platform.OS !== 'android') return;
  await Notifications.setNotificationChannelAsync('match-alerts', {
    name: 'Match alerts',
    importance: Notifications.AndroidImportance.HIGH,
    sound: 'default',
    vibrationPattern: [0, 250, 250, 250],
    lightColor: VOLT,
  });
}

function immediateTrigger(): Notifications.NotificationTriggerInput {
  // Android needs the channel; iOS fires immediately on a null trigger.
  return (Platform.OS === 'android'
    ? { channelId: 'match-alerts' }
    : null) as Notifications.NotificationTriggerInput;
}

// Bidi isolate so a mixed "CODE 2-1 CODE" scoreline keeps its visual order
// inside an Arabic (RTL) notification shade instead of jumbling the digits.
const LRI = '⁦'; // LEFT-TO-RIGHT ISOLATE
const PDI = '⁩'; // POP DIRECTIONAL ISOLATE
const ARABIC_INDIC = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'] as const;

/** Western digits → Arabic-Indic for ar, matching the app's numerals elsewhere. */
function localizeScore(value: number): string {
  if (i18n.language !== 'ar') return String(value);
  return String(value).replace(/[0-9]/g, (d) => ARABIC_INDIC[Number(d)]);
}

/** Compact, bidi-isolated scoreline: "POR 2-1 BRA". */
function liveScoreline(alert: GoalAlert): string {
  const score = `${localizeScore(alert.home)}-${localizeScore(alert.away)}`;
  return `${LRI}${alert.homeShort} ${score} ${alert.awayShort}${PDI}`;
}

/**
 * Fire an immediate local "GOAL!" notification. Premium structure to match the
 * server pushes: title "⚽ GOAL! · POR 2-1 BRA" (scoreline rides in the title so
 * the banner reads at a glance); body names the scorer when known, else repeats
 * the clean scoreline. Never throws — a gated/failed fire is silently skipped.
 */
export async function notifyGoal(alert: GoalAlert): Promise<void> {
  try {
    if (!(await notificationsEnabled())) return;
    await ensureChannel();
    const scoreLine = liveScoreline(alert);
    const title = `${i18n.t('liveAlerts.goalTitle')} · ${scoreLine}`;
    const body = alert.scorer
      ? `${LRI}${alert.scorer} ⚽${PDI}`
      : scoreLine;
    await Notifications.scheduleNotificationAsync({
      content: {
        title,
        body,
        color: VOLT,
        // Thread the fixtureId so a tap deep-links to the match (routeFromNotification
        // follows any UUID data.fixtureId). Only set when known.
        data: { type: 'live-goal', ...(alert.fixtureId ? { fixtureId: alert.fixtureId } : {}) },
      },
      trigger: immediateTrigger(),
    });
  } catch {
    // Best-effort: never let an alert break the live tracker.
  }
}

/**
 * Fire an immediate local red-card notification: title "🟥 Red Card", body the
 * player's name (falls back to the title when the scorer is unknown). Never throws.
 */
export async function notifyRedCard(alert: RedCardAlert): Promise<void> {
  try {
    if (!(await notificationsEnabled())) return;
    await ensureChannel();
    const title = i18n.t('liveAlerts.redCardTitle');
    const body = alert.player
      ? i18n.t('liveAlerts.redCardBody', { player: alert.player })
      : title;
    await Notifications.scheduleNotificationAsync({
      content: {
        title,
        body,
        color: '#FB7185',
        // Thread the fixtureId so a tap deep-links to the match. Only set when known.
        data: { type: 'live-red-card', ...(alert.fixtureId ? { fixtureId: alert.fixtureId } : {}) },
      },
      trigger: immediateTrigger(),
    });
  } catch {
    // Best-effort.
  }
}
